import { useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  EBundlesViewModel,
  EDailyBonusBadgeInternalViewModel,
  EDailyBonusInternalViewModel,
  EHeaderInternalViewModel,
  EPlayerLevelInternalViewModel,
  EProgressBarInternalViewModel,
  ERollingOffersInternalViewModel
} from '@appcharge/shared-ui';
import { Divider, Grid, Stack } from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import {
  mapRollingOffers,
  mapViewModelRollingDisplayName
} from 'utils/mapRollingOffers';
import {
  mapSpecialOffers,
  mapViewModelDisplayName
} from 'utils/mapSpecialOffers';

import { defaultTheme } from 'components/Theme/defaults';

import useImages from '../../../api/useImages';
import useTheme from '../../../api/useTheme';
import {
  BannerAspectRatio,
  EBalancesTheme,
  EBundlesInternalViewModel,
  ESectionViewModel,
  ESpecialOfferInternalViewModel,
  StoreBodyBlock
} from '../../../constants/enums';
import { uiActions } from '../../../store/uiSlice';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import AcCard from '../../AcCard/AcCard';
import AcSelect from '../../AcSelect/AcSelect';
import ActionButton from '../../ActionButton/ActionButton';

import { BodyBlocks } from './BodyBlocks/BodyBlocks';
import { ThemeDesignsSelect } from './ThemeDesignsSelect';

import '../../../style/forms.scss';
import '../../Theme/style.scss';

const mapBundleTypesBasedOnLayout: Record<EBundlesViewModel, any[]> = {
  [EBundlesViewModel.LINEAR]: [
    {
      content: 'BebopBee',
      key: EBundlesInternalViewModel.AK,
      value: EBundlesInternalViewModel.AK
    },
    {
      content: 'Main Sub',
      key: EBundlesInternalViewModel.MAIN_SUB,
      value: EBundlesInternalViewModel.MAIN_SUB
    },
    {
      content: 'Baba',
      key: EBundlesInternalViewModel.BABA,
      value: EBundlesInternalViewModel.BABA
    },
    {
      content: 'PM - Jackpot Slots',
      key: EBundlesInternalViewModel.JACKPOT_SLOTS,
      value: EBundlesInternalViewModel.JACKPOT_SLOTS
    },
    {
      content: 'PM - Big Fish Casino',
      key: EBundlesInternalViewModel.BIG_FISH_CASINO,
      value: EBundlesInternalViewModel.BIG_FISH_CASINO
    },
    {
      content: 'Me2on',
      key: EBundlesInternalViewModel.ME2ON,
      value: EBundlesInternalViewModel.ME2ON
    },
    {
      content: 'Playsimple',
      key: EBundlesInternalViewModel.PLAY_SIMPLE,
      value: EBundlesInternalViewModel.PLAY_SIMPLE
    },
    {
      content: 'Ghost Studio Classic Vegas Slots',
      key: EBundlesInternalViewModel.GHOST,
      value: EBundlesInternalViewModel.GHOST
    },
    {
      content: 'Ghost Studio Double Hit Casino',
      key: EBundlesInternalViewModel.GHOST_DOUBLE_HIT_CASINO,
      value: EBundlesInternalViewModel.GHOST_DOUBLE_HIT_CASINO
    },
    {
      content: 'Bingo Pop',
      key: EBundlesInternalViewModel.BINGO_POP,
      value: EBundlesInternalViewModel.BINGO_POP
    },
    {
      content: 'Spinx JC',
      key: EBundlesInternalViewModel.SPIN_X,
      value: EBundlesInternalViewModel.SPIN_X
    },
    {
      content: 'Spinx CF',
      key: EBundlesInternalViewModel.CASH_FRENZY,
      value: EBundlesInternalViewModel.CASH_FRENZY
    },
    {
      content: 'Banditos',
      key: EBundlesInternalViewModel.BANDITOS,
      value: EBundlesInternalViewModel.BANDITOS
    },
    {
      content: 'Royal Blast',
      key: EBundlesInternalViewModel.ROYAL_BLAST,
      value: EBundlesInternalViewModel.ROYAL_BLAST
    },
    {
      content: 'Studio',
      key: EBundlesInternalViewModel.STUDIO_DEFAULT_LINEAR,
      value: EBundlesInternalViewModel.STUDIO_DEFAULT_LINEAR
    },
    {
      content: 'Drimage',
      key: EBundlesInternalViewModel.DRIMAGE,
      value: EBundlesInternalViewModel.DRIMAGE
    }
  ],
  [EBundlesViewModel.TWO_IN_ONE]: [
    {
      content: 'Loco Bingo',
      key: EBundlesInternalViewModel.LOCO_BINGO,
      value: EBundlesInternalViewModel.LOCO_BINGO
    },
    {
      content: 'PM Cashman Casino',
      key: EBundlesInternalViewModel.CASHMAN_CASINO,
      value: EBundlesInternalViewModel.CASHMAN_CASINO
    },
    {
      content: 'PM - Lightning Link Casino',
      key: EBundlesInternalViewModel.LIGHTNING_LINK_CASINO,
      value: EBundlesInternalViewModel.LIGHTNING_LINK_CASINO
    },
    {
      content: 'PM - Heart Of Vegas',
      key: EBundlesInternalViewModel.HEART_OF_VEGAS,
      value: EBundlesInternalViewModel.HEART_OF_VEGAS
    },
    {
      content: 'PM - Mighty Fu Casino',
      key: EBundlesInternalViewModel.MIGHTY_FU_CASINO,
      value: EBundlesInternalViewModel.MIGHTY_FU_CASINO
    },
    {
      content: 'Fabel Town',
      key: EBundlesInternalViewModel.FABEL_TOWN,
      value: EBundlesInternalViewModel.FABEL_TOWN
    },
    {
      content: 'Studio',
      key: EBundlesInternalViewModel.STUDIO_DEFAULT_TWO_IN_ONE,
      value: EBundlesInternalViewModel.STUDIO_DEFAULT_TWO_IN_ONE
    }
  ],
  [EBundlesViewModel.THREE_IN_ONE]: [
    {
      content: 'Communix',
      key: EBundlesInternalViewModel.COMMUNIX,
      value: EBundlesInternalViewModel.COMMUNIX
    },
    {
      content: 'Communix 2.0',
      key: EBundlesInternalViewModel.COMMUNIX2,
      value: EBundlesInternalViewModel.COMMUNIX2
    },
    {
      content: 'Candivore',
      key: EBundlesInternalViewModel.MATCH,
      value: EBundlesInternalViewModel.MATCH
    },
    {
      content: 'Candivore 2.0',
      key: EBundlesInternalViewModel.CANDIVORE,
      value: EBundlesInternalViewModel.CANDIVORE
    },
    {
      content: 'Bagelcode',
      key: EBundlesInternalViewModel.BAGELCODE,
      value: EBundlesInternalViewModel.BAGELCODE
    },
    {
      content: 'Bagelcode 2.0',
      key: EBundlesInternalViewModel.BAGELCODE2,
      value: EBundlesInternalViewModel.BAGELCODE2
    },
    {
      content: 'Pipa',
      key: EBundlesInternalViewModel.PIPA,
      value: EBundlesInternalViewModel.PIPA
    },
    {
      content: 'Studio',
      key: EBundlesInternalViewModel.STUDIO_DEFAULT_THREE_IN_ONE,
      value: EBundlesInternalViewModel.STUDIO_DEFAULT_THREE_IN_ONE
    },
    {
      content: 'Load Complete',
      key: EBundlesInternalViewModel.LOAD_COMPLETE,
      value: EBundlesInternalViewModel.LOAD_COMPLETE
    },
    {
      content: 'Royal Match',
      key: EBundlesInternalViewModel.ROYAL_MATCH,
      value: EBundlesInternalViewModel.ROYAL_MATCH
    }
  ],
  [EBundlesViewModel.SIX_IN_ONE]: [
    {
      content: 'Whispers',
      key: EBundlesInternalViewModel.WHISPERS,
      value: EBundlesInternalViewModel.WHISPERS
    }
  ],
  [EBundlesViewModel.TWO_IN_ONE_DYNAMIC]: [
    {
      content: 'PM - Lightning Link Casino V2',
      key: EBundlesInternalViewModel.LIGHTNING_LINK_CASINO2,
      value: EBundlesInternalViewModel.LIGHTNING_LINK_CASINO2
    },
    {
      content: 'PM - Heart Of Vegas V2',
      key: EBundlesInternalViewModel.DYNAMIC_HEART_OF_VEGAS,
      value: EBundlesInternalViewModel.DYNAMIC_HEART_OF_VEGAS
    },
    {
      content: 'PM - Mighty Fu Casino V2',
      key: EBundlesInternalViewModel.DYNAMIC_MIGHTY_FU_CASINO,
      value: EBundlesInternalViewModel.DYNAMIC_MIGHTY_FU_CASINO
    },
    {
      content: 'PM - CashMan Casino V2',
      key: EBundlesInternalViewModel.DYNAMIC_CASH_MAN_CASINO,
      value: EBundlesInternalViewModel.DYNAMIC_CASH_MAN_CASINO
    },
    {
      content: 'Deca',
      key: EBundlesInternalViewModel.DECA,
      value: EBundlesInternalViewModel.DECA
    },
    {
      content: 'Playdog Soft',
      key: EBundlesInternalViewModel.PLAYDOG,
      value: EBundlesInternalViewModel.PLAYDOG
    },
    {
      content: 'Junes Journey',
      key: EBundlesInternalViewModel.JUNES_JOURNEY,
      value: EBundlesInternalViewModel.JUNES_JOURNEY
    },
    {
      content: 'Candy Crush',
      key: EBundlesInternalViewModel.CANDY_CRUSH,
      value: EBundlesInternalViewModel.CANDY_CRUSH
    }
  ]
};
const sectionThemes = [
  {
    content: 'Default',
    key: ESectionViewModel.DEFAULT,
    value: ESectionViewModel.DEFAULT
  },
  {
    content: 'Bagelcode',
    key: ESectionViewModel.BAGELCODE,
    value: ESectionViewModel.BAGELCODE
  },
  {
    content: 'Pipa',
    key: ESectionViewModel.PIPA,
    value: ESectionViewModel.PIPA
  },
  {
    content: 'Whispers',
    key: ESectionViewModel.WHISPERS,
    value: ESectionViewModel.WHISPERS
  },
  {
    content: 'LLC',
    key: ESectionViewModel.LLC,
    value: ESectionViewModel.LLC
  },
  {
    content: 'MightyFu',
    key: ESectionViewModel.MIGHTY_FU_CASINO,
    value: ESectionViewModel.MIGHTY_FU_CASINO
  },
  {
    content: 'Ghost',
    key: ESectionViewModel.GHOST,
    value: ESectionViewModel.GHOST
  },
  {
    content: 'Fabel Town',
    key: ESectionViewModel.FABEL_TOWN,
    value: ESectionViewModel.FABEL_TOWN
  },
  {
    content: 'Section Image',
    key: ESectionViewModel.SECTION_IMAGE,
    value: ESectionViewModel.SECTION_IMAGE
  }
];
const balanceThemes = [
  {
    content: 'Default',
    key: EBalancesTheme.DEFAULT,
    value: EBalancesTheme.DEFAULT
  },
  {
    content: 'BagelCode',
    key: EBalancesTheme.BAGELCODE,
    value: EBalancesTheme.BAGELCODE
  },
  {
    content: 'SpaceGO',
    key: EBalancesTheme.SPACEGO,
    value: EBalancesTheme.SPACEGO
  },
  {
    content: 'Playsimple',
    key: EBalancesTheme.PLAYSIMPLE,
    value: EBalancesTheme.PLAYSIMPLE
  },
  {
    content: 'Me2on',
    key: EBalancesTheme.ME2ON,
    value: EBalancesTheme.ME2ON
  },
  {
    content: 'Pipa',
    key: EBalancesTheme.PIPA,
    value: EBalancesTheme.PIPA
  },
  {
    content: 'Whipers',
    key: EBalancesTheme.WHISPERS,
    value: EBalancesTheme.WHISPERS
  },
  {
    content: 'Ghost Studio Classic Vegas Slots',
    key: EBalancesTheme.GHOST,
    value: EBalancesTheme.GHOST
  },
  {
    content: 'Ghost Studio Double Hit Casino',
    key: EBalancesTheme.GHOST_DOUBLE_HIT_CASINO,
    value: EBalancesTheme.GHOST_DOUBLE_HIT_CASINO
  },
  {
    content: 'Jam City - Bingo Pop',
    key: EBalancesTheme.BINGO_POP,
    value: EBalancesTheme.BINGO_POP
  },
  {
    content: 'Deca',
    key: EBalancesTheme.DECA,
    value: EBalancesTheme.DECA
  },
  {
    content: 'Spin X',
    key: EBalancesTheme.SPIN_X,
    value: EBalancesTheme.SPIN_X
  },
  {
    content: 'Cash Frenzy',
    key: EBalancesTheme.CASH_FRENZY,
    value: EBalancesTheme.CASH_FRENZY
  },
  {
    content: 'Load Complete',
    key: EBalancesTheme.LOAD_COMPLETE,
    value: EBalancesTheme.LOAD_COMPLETE
  },
  {
    content: 'Playdog',
    key: EBalancesTheme.PLAYDOG,
    value: EBalancesTheme.PLAYDOG
  },
  {
    content: 'Banditos',
    key: EBalancesTheme.BANDITOS,
    value: EBalancesTheme.BANDITOS
  },
  {
    content: 'Royal Match',
    key: EBalancesTheme.ROYAL_MATCH,
    value: EBalancesTheme.ROYAL_MATCH
  }
];

const StoreLayout = () => {
  const { publisherId } = useParams();
  const pictures = useImages(publisherId).getImages;
  const themeActions = useTheme(publisherId);
  const { data } = themeActions.getStoreTheme;
  const { featureFlags } = useFeatureFlags({ publisherId });
  const dispatch = useDispatch();

  const initialBodyBlocks = useMemo(() => {
    if (!data?.storeTemplate) return;

    if (!data?.storeTemplate?.bodyBlocksOrder?.length) {
      return defaultTheme.storeTemplate.bodyBlocksOrder.filter(
        (block) => block !== StoreBodyBlock.richText
      );
    }
    return data.storeTemplate.bodyBlocksOrder;
  }, [data?.storeTemplate]);

  const storeTemplateForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      bodyBlocksOrder: initialBodyBlocks
    },
    onSubmit: async (values) => {
      try {
        await themeActions.updateStoreTheme.mutateAsync({
          storeTemplate: values
        });
        showToast({
          message: 'Store components layout updated',
          status: EToasterStatus.SUCCESS
        });
        dispatch(uiActions.setUnsavedChanges(false));
      } catch (error) {
        showToast({
          message: 'Error updating store components layout',
          status: EToasterStatus.ERROR,
          description:
            ((error as AxiosError).response?.data as { message: string })
              .message || 'Unknown error occurred'
        });
      }
    }
  });

  const generalForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      bundlesViewModel:
        data?.general?.bundlesViewModel ||
        defaultTheme?.general?.bundlesViewModel,
      bundlesInternalViewModel:
        data?.general?.bundlesInternalViewModel ||
        defaultTheme?.general?.bundlesInternalViewModel,
      specialOffersViewModel:
        data?.general?.specialOffersViewModel ||
        defaultTheme.general.specialOffersViewModel,
      specialOffersInternalViewModel:
        data?.general?.specialOffersInternalViewModel ||
        defaultTheme.general?.specialOffersInternalViewModel,
      rollingOffersInternalViewModel:
        data?.general?.rollingOffersInternalViewModel ||
        defaultTheme.general?.rollingOffersInternalViewModel,
      headerInternalViewModel:
        data?.general?.headerInternalViewModel ||
        defaultTheme.general?.headerInternalViewModel,
      dailyBonusInternalViewModel:
        data?.general?.dailyBonusInternalViewModel ||
        defaultTheme.general?.dailyBonusInternalViewModel,
      dailyBonusBadgeInternalViewModel:
        data?.general?.dailyBonusBadgeInternalViewModel ||
        defaultTheme.general?.dailyBonusBadgeInternalViewModel,
      playerLevelInternalViewModel:
        data?.general?.playerLevelInternalViewModel ||
        defaultTheme.general?.playerLevelInternalViewModel,
      progressBarInternalViewModel:
        data?.general?.progressBarInternalViewModel ||
        defaultTheme.general?.progressBarInternalViewModel,
      balancesTheme:
        data?.general?.balancesTheme || defaultTheme?.general?.balancesTheme,
      sectionViewModel:
        data?.general?.sectionViewModel ||
        defaultTheme.general.sectionViewModel,
      popupRatio: data?.general?.popupRatio || defaultTheme.general?.popupRatio
    },
    onSubmit: async (values) => {
      try {
        await themeActions.updateGeneralTheme.mutateAsync(values);
        showToast({
          message: 'Template settings updated',
          status: EToasterStatus.SUCCESS
        });
        dispatch(uiActions.setUnsavedChanges(false));
      } catch (error) {
        showToast({
          message: 'Error updating template settings',
          status: EToasterStatus.ERROR,
          description:
            ((error as AxiosError).response?.data as { message: string })
              .message || 'Unknown error occurred'
        });
      }
    }
  });

  const specialOffersOptions = useMemo(
    () =>
      (Object.keys(mapSpecialOffers) as ESpecialOfferInternalViewModel[]).map(
        (specialOfferModelName) => ({
          content: mapViewModelDisplayName[specialOfferModelName],
          key: specialOfferModelName,
          value: specialOfferModelName
        })
      ),
    []
  );

  const rollingOffersOptions = useMemo(
    () =>
      (Object.keys(mapRollingOffers) as ERollingOffersInternalViewModel[]).map(
        (rollingOfferModelName) => ({
          content: mapViewModelRollingDisplayName[rollingOfferModelName],
          key: rollingOfferModelName,
          value: rollingOfferModelName
        })
      ),
    []
  );

  const headerOptions = useMemo(
    () =>
      (
        Object.values(EHeaderInternalViewModel) as EHeaderInternalViewModel[]
      ).map((headerModelName) => ({
        content: headerModelName,
        key: headerModelName,
        value: headerModelName
      })),
    []
  );

  const dailyBonusOptions = useMemo(
    () =>
      (
        Object.values(
          EDailyBonusInternalViewModel
        ) as EDailyBonusInternalViewModel[]
      ).map((dailyBonusModelName) => ({
        content: dailyBonusModelName,
        key: dailyBonusModelName,
        value: dailyBonusModelName
      })),
    []
  );

  const dailyBonusBadgeOptions = useMemo(
    () =>
      (
        Object.values(
          EDailyBonusBadgeInternalViewModel
        ) as EDailyBonusBadgeInternalViewModel[]
      ).map((dailyBonusBadgeModelName) => ({
        content: dailyBonusBadgeModelName,
        key: dailyBonusBadgeModelName,
        value: dailyBonusBadgeModelName
      })),
    []
  );

  const playerLevelOptions = useMemo(
    () =>
      (
        Object.values(
          EPlayerLevelInternalViewModel
        ) as EPlayerLevelInternalViewModel[]
      ).map((playerLevelModelName) => ({
        content: playerLevelModelName,
        key: playerLevelModelName,
        value: playerLevelModelName
      })),
    []
  );

  const progressBarOptions = useMemo(
    () =>
      (
        Object.values(
          EProgressBarInternalViewModel
        ) as EProgressBarInternalViewModel[]
      ).map((progressBarModelName) => ({
        content: progressBarModelName,
        key: progressBarModelName,
        value: progressBarModelName
      })),
    []
  );

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        {!pictures.isLoading && (
          <Stack>
            <ThemeDesignsSelect />
            <BodyBlocks
              currentFormValue={storeTemplateForm.values.bodyBlocksOrder}
              handleChange={(newArr: string[]) =>
                storeTemplateForm.setFieldValue('bodyBlocksOrder', newArr)
              }
              submitChange={storeTemplateForm.submitForm}
              featureFlags={featureFlags}
              isDirty={storeTemplateForm.dirty}
            />
            <AcCard stackContainer={false} title="Special Offers">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Special Offers Internal"
                    name="specialOffersInternalViewModel"
                    value={generalForm.values.specialOffersInternalViewModel}
                    items={specialOffersOptions}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.specialOffersInternalViewModel &&
                      Boolean(generalForm.errors.specialOffersInternalViewModel)
                    }
                    tooltip="This will be the layout of your special offers."
                  />
                </Grid>
                <Grid item xs={12}>
                  <AcSelect
                    header="Special Offers"
                    name="specialOffersViewModel"
                    value={generalForm.values.specialOffersViewModel}
                    items={[
                      {
                        content: generalForm.values.specialOffersViewModel,
                        key: generalForm.values.specialOffersViewModel,
                        value: generalForm.values.specialOffersViewModel
                      }
                    ]}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.specialOffersViewModel &&
                      Boolean(generalForm.errors.specialOffersViewModel)
                    }
                    tooltip="This will be the layout of your special offers."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />

            <AcCard stackContainer={false} title="Bundles">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Store Template"
                    name="bundlesViewModel"
                    value={generalForm.values.bundlesViewModel}
                    items={[
                      {
                        content: '1 in a row',
                        key: EBundlesViewModel.LINEAR,
                        value: EBundlesViewModel.LINEAR
                      },
                      {
                        content: '2 in a row',
                        key: EBundlesViewModel.TWO_IN_ONE,
                        value: EBundlesViewModel.TWO_IN_ONE
                      },
                      {
                        content: '3 in a row',
                        key: EBundlesViewModel.THREE_IN_ONE,
                        value: EBundlesViewModel.THREE_IN_ONE
                      },
                      {
                        content: '6 in a row',
                        key: EBundlesViewModel.SIX_IN_ONE,
                        value: EBundlesViewModel.SIX_IN_ONE
                      },
                      {
                        content: '2 in a row (dynamic height)',
                        key: EBundlesViewModel.TWO_IN_ONE_DYNAMIC,
                        value: EBundlesViewModel.TWO_IN_ONE_DYNAMIC
                      }
                    ]}
                    onChange={(e) => {
                      generalForm.setFieldValue(
                        'bundlesInternalViewModel',
                        mapBundleTypesBasedOnLayout[
                          e.target.value as EBundlesViewModel
                        ][0].key
                      );
                      generalForm.handleChange(e);
                    }}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.bundlesViewModel &&
                      Boolean(generalForm.errors.bundlesViewModel)
                    }
                    tooltip="This will be the layout of your store."
                  />
                </Grid>
                <Grid item xs={12}>
                  <AcSelect
                    header="Bundle Template"
                    name="bundlesInternalViewModel"
                    value={generalForm.values.bundlesInternalViewModel}
                    items={
                      mapBundleTypesBasedOnLayout[
                        generalForm.values.bundlesViewModel
                      ]
                    }
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.bundlesInternalViewModel &&
                      Boolean(generalForm.errors.bundlesInternalViewModel)
                    }
                    tooltip="This will be the layout of your bundles."
                  />
                </Grid>
                <Grid item xs={12}>
                  <AcSelect
                    header="Section Theme"
                    name="sectionViewModel"
                    value={generalForm.values.sectionViewModel}
                    items={sectionThemes}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.sectionViewModel &&
                      Boolean(generalForm.errors.sectionViewModel)
                    }
                    tooltip="This will be the layout of your sections."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Rolling Offers">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Rolling Offers Internal"
                    name="rollingOffersInternalViewModel"
                    value={generalForm.values.rollingOffersInternalViewModel}
                    items={rollingOffersOptions}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.rollingOffersInternalViewModel &&
                      Boolean(generalForm.errors.rollingOffersInternalViewModel)
                    }
                    tooltip="This will be the layout of your rolling offers."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Header">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Header Internal"
                    name="headerInternalViewModel"
                    value={generalForm.values.headerInternalViewModel}
                    items={headerOptions}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.headerInternalViewModel &&
                      Boolean(generalForm.errors.headerInternalViewModel)
                    }
                    tooltip="This will be the layout of your rolling offers."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Daily Bonus">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Daily Bonus Internal"
                    name="dailyBonusInternalViewModel"
                    value={generalForm.values.dailyBonusInternalViewModel}
                    items={dailyBonusOptions}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.dailyBonusInternalViewModel &&
                      Boolean(generalForm.errors.dailyBonusInternalViewModel)
                    }
                    tooltip="This will be the layout of your rolling offers."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Daily Bonus Badge">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Daily Bonus Badge Internal"
                    name="dailyBonusBadgeInternalViewModel"
                    value={generalForm.values.dailyBonusBadgeInternalViewModel}
                    items={dailyBonusBadgeOptions}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.dailyBonusBadgeInternalViewModel &&
                      Boolean(
                        generalForm.errors.dailyBonusBadgeInternalViewModel
                      )
                    }
                    tooltip="This will be the layout of your rolling offers."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Player Level">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Player Level Internal"
                    name="playerLevelInternalViewModel"
                    value={generalForm.values.playerLevelInternalViewModel}
                    items={playerLevelOptions}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.playerLevelInternalViewModel &&
                      Boolean(generalForm.errors.playerLevelInternalViewModel)
                    }
                    tooltip="This will be the layout of your player level."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Progress Bar">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Progress Bar Internal"
                    name="progressBarInternalViewModel"
                    value={generalForm.values.progressBarInternalViewModel}
                    items={progressBarOptions}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.progressBarInternalViewModel &&
                      Boolean(generalForm.errors.progressBarInternalViewModel)
                    }
                    tooltip="This will be the layout of your progress bar."
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Popups">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Popup ratio"
                    name="popupRatio"
                    value={generalForm.values.popupRatio}
                    items={[
                      {
                        content: '1:1',
                        key: BannerAspectRatio.SQUARE,
                        value: BannerAspectRatio.SQUARE
                      },
                      {
                        content: '2:3',
                        key: BannerAspectRatio.TWO_ON_THREE,
                        value: BannerAspectRatio.TWO_ON_THREE
                      }
                    ]}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    tooltip="This will be the ratio of your popups"
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard stackContainer={false} title="Balance">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Balance Theme"
                    name="balancesTheme"
                    value={generalForm.values.balancesTheme}
                    placeholder="Select"
                    items={balanceThemes}
                    onChange={generalForm.handleChange}
                    onBlur={generalForm.handleBlur}
                    error={
                      generalForm.touched.balancesTheme &&
                      Boolean(generalForm.errors.balancesTheme)
                    }
                  />
                </Grid>
              </Grid>
            </AcCard>
          </Stack>
        )}
        <Grid item xs={6} mt={3} mb={3}>
          <ActionButton
            variant="outlined"
            text="Update"
            onClick={generalForm.submitForm}
            disabled={
              !generalForm.isValid ||
              !generalForm.dirty ||
              generalForm.isSubmitting
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default StoreLayout;
